import React, { useState, useEffect, useRef } from "react";
import { IconDownload, IconLoader, IconPlayerPlay, IconPlayerPause } from "@tabler/icons-react";
import * as XLSX from "xlsx";

const ExportButton = ({
  sessions = [],
  filterOptions = {},
  bookings = [],
  summary = {},
  className = "btn btn-outline-success"
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [autoExportEnabled, setAutoExportEnabled] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('11:00'); // Default 11:00 AM
  const [nextExportTime, setNextExportTime] = useState(null);
  const timeoutRef = useRef(null);

  // Calculate next scheduled export time
  const calculateNextExportTime = () => {
    const now = new Date();
    const [hours, minutes] = scheduledTime.split(':').map(Number);

    const scheduledDate = new Date(now);
    scheduledDate.setHours(hours, minutes, 0, 0);

    // If the scheduled time has already passed today, schedule for tomorrow
    if (scheduledDate <= now) {
      scheduledDate.setDate(scheduledDate.getDate() + 1);
    }

    return scheduledDate;
  };

  // Auto-export functionality
  useEffect(() => {
    const scheduleNextExport = () => {
      if (!autoExportEnabled) return;

      const nextTime = calculateNextExportTime();
      setNextExportTime(nextTime);

      // Calculate delay until next export
      const delay = nextTime.getTime() - Date.now();

      // Schedule the export
      timeoutRef.current = setTimeout(async () => {
        console.log(`Scheduled export triggered at ${new Date().toLocaleString()}`);
        await handleExport();

        // After export completes, schedule the next one
        scheduleNextExport();
      }, delay);
    };

    if (autoExportEnabled) {
      scheduleNextExport();
    } else {
      // Clear timeout when disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setNextExportTime(null);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [autoExportEnabled, scheduledTime]);

  const toggleAutoExport = () => {
    setAutoExportEnabled(!autoExportEnabled);
  };

  const handleTimeChange = (e) => {
    setScheduledTime(e.target.value);
  };

  const handleExport = async () => {
    if (isLoading) return; // Prevent multiple clicks

    setIsLoading(true);
    try {
      const workbook = XLSX.utils.book_new();
      const play = summary.play || {};
      const finished = summary.finished || {};
      const reconfirmed = summary.reconfirmed || {};

      // Create Summary Sheet
      const summaryData = [
        ['Export Date', new Date().toLocaleString()],
        ['Reservation Date', filterOptions.filters?.reservation_date || ''],
        ['Agent Filter', filterOptions.filters?.agent_id || 'All'],
        ['Label Filter', filterOptions.filters?.label_id || 'All'],
        ['Status Filter', filterOptions.filters?.status || 'All'],
        [''],
        ['SUMMARY STATISTICS'],
        ['Category', 'ATV Single', 'ATV Tandem', 'Total PAX'],
        ['Total Play', play.atv_single ?? 0, play.atv_tandem ?? 0, play.total_pax ?? 0],
        ['Total Finished', finished.atv_single ?? 0, finished.atv_tandem ?? 0, finished.total_pax ?? 0],
        ['Total Reconfirmed', reconfirmed.atv_single ?? 0, reconfirmed.atv_tandem ?? 0, reconfirmed.total_pax ?? 0],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // Group bookings by trip (same order as displayed)
      const bookingsByTrip = {};
      if (bookings?.data) {
        // Sort sessions to maintain display order
        const sortedSessions = sessions.sort((a, b) => a.id - b.id);

        sortedSessions.forEach(session => {
          const tripBookings = bookings.data.filter(booking => booking.trip_id === session.id);
          if (tripBookings.length > 0) {
            bookingsByTrip[session.title] = tripBookings;
          }
        });
      }

      // Create sheet for each trip
      Object.entries(bookingsByTrip).forEach(([tripName, tripBookings]) => {
        const headers = [
          'No',
          'Code',
          'Guest Name',
          'Status',
          'PAX',
          'Transport',
          'Agent Name',
          'Customer/Hotel',
          'Nationality',
          'Phone',
          'Email',
          'Guest Mark',
          'Driver',
          'Labels',
          'Activities',
          'Total Price',
          'Net Price',
          'Payment Method',
          'Payment Note',
          'Last 4 Digits',
          'Note',
          'Host By',
          'Guide',
          'Photographer',
          'Track',
          'PU Time',
          'PU Location',
          'PU Price',
          'Reservation Date',
          'Trip Name',
          'Trip Time',
          'Created At',
          'Updated At'
        ];

        const rows = tripBookings.map((booking, index) => {
          // Extract activities
          const activities = booking.activities?.map(act =>
            `${act.activity}: ${act.qty}`
          ).join(', ') || '';

          // Extract labels
          const labels = booking.labels?.map(label =>
            label.name
          ).join(', ') || '';

          // Extract guides
          const guides = booking.guides?.map(guide =>
            guide.name
          ).join(', ') || '';

          // Extract photographers
          const photographers = booking.photographers?.map(photo =>
            photo.name
          ).join(', ') || '';

          return [
            index + 1,
            booking.code || '',
            booking.guest_name || '',
            booking.status || '',
            booking.guest_pax || 0,
            booking.guest_transport || '',
            booking.agent?.name || '',
            booking.customer?.name || '',
            booking.guest_nationality || booking.customer?.nationality || '',
            booking.customer?.phone || booking.guest_phone || '',
            booking.customer?.email || booking.guest_email || '',
            booking.guest_mark || '',
            booking.driver || '',
            labels,
            activities,
            booking.total_price || 0,
            booking.net_price_by_include_susuk || 0,
            booking.payment_method?.name || '',
            booking.payment_note || '',
            booking.last_number || '',
            booking.note || '',
            booking.host?.name || '',
            guides,
            photographers,
            booking.track || '',
            booking.guest_pu_time || '',
            booking.guest_pu_location || '',
            booking.guest_pu_price || 0,
            booking.reservation_date || '',
            booking.trip?.name || '',
            booking.trip ? `${booking.trip.start_time?.slice(0, 5)} - ${booking.trip.end_time?.slice(0, 5)}` : '',
            booking.created_at ? new Date(booking.created_at).toLocaleString() : '',
            booking.updated_at ? new Date(booking.updated_at).toLocaleString() : ''
          ];
        });

        const sheetData = [headers, ...rows];
        const sheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Auto-size columns
        const colWidths = headers.map((header, index) => ({
          wch: Math.max(header.length, ...rows.map(row => String(row[index] || '').length))
        }));
        sheet['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(workbook, sheet, tripName.substring(0, 31)); // Excel sheet name limit
      });

      // Generate filename with date
      const fileName = `booking-export-${filterOptions.filters?.reservation_date || new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file using Blob and anchor with download attribute for better Android compatibility
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      console.log('Excel export completed successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex gap-2 align-items-center">
      {/* Manual Export Button */}
      <button
        className={`${className} ${isLoading ? 'disabled' : ''}`}
        onClick={handleExport}
        disabled={isLoading}
        title={isLoading ? "Exporting..." : "Export Data"}
      >
        {isLoading ? (
          <IconLoader size={24} className="animate-spin" />
        ) : (
          <IconDownload size={24} />
        )}
      </button>

      {/* Auto-Export Controls */}
      {/* <div className="d-flex gap-2 align-items-center">
        <button
          className={`btn ${autoExportEnabled ? 'btn-success' : 'btn-outline-secondary'} btn-sm`}
          onClick={toggleAutoExport}
          title={autoExportEnabled ? "Disable Auto-Export" : "Enable Auto-Export"}
        >
          {autoExportEnabled ? (
            <IconPlayerPause size={16} />
          ) : (
            <IconPlayerPlay size={16} />
          )}
        </button>
        <div className="d-flex align-items-center gap-1">
          <input
            type="time"
            className="form-control form-control-sm"
            style={{ width: '100px' }}
            value={scheduledTime}
            onChange={handleTimeChange}
            title="Scheduled export time (daily)"
          />
        </div>
        {autoExportEnabled && nextExportTime && (
          <div className="text-muted small">
            Next: {nextExportTime.toLocaleTimeString()}
          </div>
        )}
      </div> */}
    </div>
  );
};

export default ExportButton;
